import React, { useState } from 'react';
import { Flags } from 'react-feature-flags';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';

import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import HealthAndSafetyOutlined from '@mui/icons-material/HealthAndSafetyOutlined';
import MarkEmailReadOutlined from '@mui/icons-material/MarkEmailReadOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';

import { useTranslation } from 'react-i18next';
import { Loader } from '@omnigenbiodata/ui';

import PortalLayout from '../../../../layout/Portal';
import { useAppSelector, useAppDispatch } from '../../../../store';
import { updateNotifyNewProductsThunk, updateHealthDataOptInThunk } from '../../../../store/participant/';
import {
  healthDataOptInSelector,
  notifyNewProductsSelector,
  isBusySelector,
  hasErrorSelector,
  withdrawalOptionSelector,
} from '../../../../store/participant/selectors';

import UpdateCommsForm from './components/UpdateCommsForm';
import UpdateHealthForm from './components/UpdateHealthForm';
import WithdrawalOption from './components/WithdrawalOption';
import { FEATURES } from '../../../../core/constants/features.constants';
import { chartColours } from '../../../../core/constants/colours.constants';
import PageContainer from '../../../../components/content/PageContainer';
import PageSection from '../../../../components/content/PageSection';

function PreferencesScene() {
  const { t } = useTranslation('portal');
  const dispatch = useAppDispatch();

  const [hasSubmitted, setHasSubmitted] = useState(false);

  const healthDataOptIn = useAppSelector(healthDataOptInSelector);
  const notifyNewProducts = useAppSelector(notifyNewProductsSelector);
  const withdrawalOption = useAppSelector(withdrawalOptionSelector);
  const isBusy = useAppSelector(isBusySelector);
  const hasError = useAppSelector(hasErrorSelector);

  const handleUpdateHealthSubmit = (values: any) => {
    setHasSubmitted(true);
    dispatch(updateHealthDataOptInThunk(values.healthDataOptIn));
  };

  const handleUpdateCommsSubmit = (values: any) => {
    setHasSubmitted(true);
    dispatch(updateNotifyNewProductsThunk(values.notifyNewProducts));
  };

  return (
    <>
      <PortalLayout>
        <PageContainer
          title={t('preferences.title')}
          icon={
            <Box
              sx={{
                width: 54,
                height: 54,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${chartColours[3]}26`,
                color: chartColours[3],
                flexShrink: 0,
                border: 1,
                borderColor: `${chartColours[3]}55`,
              }}
            >
              <TuneOutlined sx={{ fontSize: 34 }} />
            </Box>
          }
        >
          {hasSubmitted && !isBusy && hasError && (
            <Alert
              severity="error"
              icon={<ErrorOutlineOutlined />}
              sx={{
                mb: 3,
                borderRadius: 3,
                border: 1,
                borderColor: 'error.light',
                alignItems: 'center',
                '& .MuiAlert-message': {
                  fontWeight: 700,
                },
              }}
            >
              {t('preferences.messages.error')}
            </Alert>
          )}

          <PageSection title={t('home.healthInfoLink')} colour={chartColours[1]} icon={<HealthAndSafetyOutlined />}>
            <UpdateHealthForm
              onSubmit={handleUpdateHealthSubmit}
              initialValues={{
                healthDataOptIn: healthDataOptIn || false,
              }}
            />
          </PageSection>

          <PageSection title={t('home.preferencesLink')} colour={chartColours[3]} icon={<MarkEmailReadOutlined />}>
            <UpdateCommsForm
              onSubmit={handleUpdateCommsSubmit}
              initialValues={{
                notifyNewProducts: notifyNewProducts || false,
              }}
            />
          </PageSection>

          <Flags authorizedFlags={[FEATURES.USER_WITHDRAWAL]}>
            <PageSection title={t('preferences.withdraw.title')} colour={chartColours[5]} icon={<LogoutOutlined />}>
              <WithdrawalOption selectedOption={withdrawalOption} />
            </PageSection>
          </Flags>
        </PageContainer>
      </PortalLayout>

      <Loader isVisible={isBusy} label={t('personal.saving')} />
    </>
  );
}

export default PreferencesScene;
